import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import {
  DataTable, PremiumModal, Button, Field, SelectField, StatusBadge,
} from "components/ui";

const BranchAdminsCard = () => {
  const axiosPrivate = useAxiosPrivate();
  const [admins, setAdmins] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [assignModal, setAssignModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState("");

  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchAdmins = async () => {
    try {
      const res = await axiosPrivate.get("/api/users/by-tenant?role=BRANCH_ADMIN");
      setAdmins(res.data);
    } catch (_) {
      toast.error("Failed to load branch admins");
    } finally {
      setLoading(false);
    }
  };
  const fetchBranches = async () => {
    try {
      const res = await axiosPrivate.get("/api/branches");
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to load branches", err);
    }
  };

  useEffect(() => { fetchAdmins(); fetchBranches(); }, []);

  const openEditModal = (a) => {
    setSelectedAdmin(a);
    setEditForm({ firstName: a.firstName || "", lastName: a.lastName || "", email: a.email || "", phone: a.phone || "" });
    setEditModal(true);
  };

  const handleEditSubmit = async () => {
    setSubmitting(true);
    try {
      await axiosPrivate.put(`/api/users/${selectedAdmin.id}`, editForm);
      toast.success("Admin updated");
      setEditModal(false);
      fetchAdmins();
    } catch (_) {
      toast.error("Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Delete ${admin.firstName} ${admin.lastName}?`)) return;
    try {
      await axiosPrivate.delete(`/api/users/${admin.id}`);
      toast.success("Admin deleted");
      fetchAdmins();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  const openAssignModal = (admin) => {
    setSelectedAdmin(admin);
    setSelectedBranchId(admin.branch ? admin.branch.id : "");
    setAssignModal(true);
  };

  const handleAssign = async () => {
    try {
      if (selectedBranchId) {
        await axiosPrivate.patch(`/api/users/${selectedAdmin.id}/assign-branch`, selectedBranchId);
        toast.success("Branch assigned");
      } else {
        await axiosPrivate.patch(`/api/users/${selectedAdmin.id}/unassign-branch`);
        toast.success("Branch unassigned");
      }
      setAssignModal(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message);
    }
  };

  return (
    <>
      <DataTable
        title="Branch admins"
        subtitle="Tenant-level administrators"
        loading={loading}
        data={admins}
        rowKey={(a) => a.id}
        searchPlaceholder="Search admins…"
        pageSize={8}
        emptyTitle="No branch admins yet"
        columns={[
          {
            key: "name", header: "Name",
            render: (a) => <strong>{a.firstName} {a.lastName}</strong>,
          },
          { key: "email", header: "Email", render: (a) => <span className="t-muted">{a.email}</span> },
          { key: "phone", header: "Phone", render: (a) => a.phone || "—" },
          {
            key: "branch", header: "Branch",
            render: (a) =>
              a.branch
                ? <StatusBadge tone="brand">{a.branch.name}</StatusBadge>
                : <StatusBadge tone="warning">Unassigned</StatusBadge>,
          },
          {
            key: "actions", header: "", align: "right",
            render: (a) => (
              <div style={{ display: "inline-flex", gap: 6 }}>
                <Button variant="secondary" size="sm" onClick={() => openEditModal(a)}>Edit</Button>
                <Button variant="primary" size="sm" onClick={() => openAssignModal(a)}>Branch</Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(a)}>Delete</Button>
              </div>
            ),
          },
        ]}
      />

      <PremiumModal
        isOpen={editModal}
        toggle={() => setEditModal(false)}
        title="Edit admin"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditModal(false)}>Cancel</Button>
            <Button variant="primary" loading={submitting} onClick={handleEditSubmit}>Save</Button>
          </>
        }
      >
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <Field label="First name" required value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
          <Field label="Last name"  required value={editForm.lastName}  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
          <Field label="Email" required type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
          <Field label="Phone" type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
        </div>
      </PremiumModal>

      <PremiumModal
        isOpen={assignModal}
        toggle={() => setAssignModal(false)}
        title="Assign branch"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAssignModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAssign}>Save</Button>
          </>
        }
      >
        <p className="t-body-sm" style={{ marginBottom: 16 }}>
          Admin: <strong>{selectedAdmin?.firstName} {selectedAdmin?.lastName}</strong>
        </p>
        <SelectField
          label="Select branch"
          value={selectedBranchId}
          onChange={(e) => setSelectedBranchId(e.target.value)}
        >
          <option value="">— None (unassign) —</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </SelectField>
      </PremiumModal>
    </>
  );
};

export default BranchAdminsCard;
